import React, { forwardRef, useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { get, isEmpty } from 'lodash';
import moment from 'moment';
import { StyleSheet } from 'react-native';
import { ActivityIndicator } from '@hmsexp/component-library';

import MultiSeriesLineChart from '../../../../../components/Common/MultiSeriesLineChart';
import { Text, View } from '../../../../../components/Common/react-native-components';
import ExpandableGrid from '../../../../../components/ExpandableGrid/ExapandableGridWrapper';
import Breadcrumb from './BreadCrumb';
import MultiSelect from '../../../../Filters/component/MultiSelect';
import MultiFeatureCard from '../../../../../components/Common/Card/MultiOptionsCard/MultiFeatureCard';
import LegendBar from '../LegendBar';

import useExpandableGridHelper from '../../../hooks/useExpandableGridHelper';
import {
  buildMonthSeriesForMultiLineChart,
  convertCstDataIntoTableCompatible,
  seprateDateAndNonDateColumns
} from '../../../../../utils/transformChartsAndTableData';
import { CHART_COLORS } from '../../../../../containers/OnlineReview/constants';
import { processColumns } from './utils';
import GenericNoData from '../../../../../components/Common/NoData/GenricNoData';

const TopicTrendsOverTime = forwardRef((props, ref) => {
  const { itemData, timeperiodLabel, applied_filter_values, widgetId, reduxData } = props;

  const [activeSwitch, setActiveSwitch] = useState();
  const [selectedMetricType, setSelectedMetricType] = useState([get(itemData, 'defaultMetricType')]);
  const [displayMetricType, setDisplayMetricType] = useState(selectedMetricType?.[0]?.fullValue);

  const dispatch = useDispatch();
  const metricTypesOptions = get(itemData, 'metricTypesOptions');
  const currentLevel = JSON.stringify(get(reduxData, 'currentLevel', { root: 'All Topics' }));
  const chartData = get(reduxData, 'chartData', {});
  const tableData = get(reduxData, 'tableData', {});

  const handleAggregationResponse = useCallback(
    ({ rawData, tableData, widget_config }) => {
      const topicLevel =
        widget_config.topic_filter && !isEmpty(widget_config.topic_filter)
          ? widget_config.topic_filter
          : { root: 'All Topics' };

      dispatch({
        type: 'SAVE_WIDGET_CHART_DATA',
        payload: {
          widgetId,
          metricType: widget_config.metric_type,
          chartData: rawData,
          currentLevel: JSON.stringify(topicLevel)
        }
      });
      if (widget_config?.changeLevel) {
        dispatch({
          type: 'SET_WIDGET_CURRENT_LEVEL',
          payload: { widgetId, currentLevel: topicLevel }
        });
      }
    },
    [dispatch, widgetId]
  );

  const { fetchData, loadingData, tableColumns, error } = useExpandableGridHelper({
    widgetId,
    appliedFilterValues: applied_filter_values,
    onAggregationResponseFn: handleAggregationResponse
  });

  const handleMetricChange = async selectedOption => {
    const nextMetric = selectedOption[0].fullValue;
    setSelectedMetricType(selectedOption);

    const result = await fetchData({
      widget_config: { topic_filter: {}, topic_depth: 'l1', metric_type: nextMetric }
    });
    const config = itemData?.metricBasedConfigs?.[nextMetric] || {};
    const newColumns = processColumns(result?.columns, config);

    dispatch({
      type: 'SAVE_WIDGET_TABLE_DATA',
      payload: {
        widgetId,
        metricType: nextMetric,
        tableData: result?.tableData,
        tableColumns: newColumns,
        cacheKey: 'none-none-1'
      }
    });

    dispatch({
      type: 'SET_WIDGET_CURRENT_LEVEL',
      payload: { widgetId, currentLevel: { root: 'All Topics' } }
    });

    if (result) setDisplayMetricType(nextMetric);
  };

  const fetchNestedData = row => {
    const currentDepth = get(row, 'topic_depth', 'l1');
    const nextDepth = currentDepth === 'l1' ? 'l2' : 'l3';
    const topic_filter = get(row, 'topic_filter', {});
    const topicName = get(row, 'primary', '');

    const widget_config = {
      topic_depth: nextDepth,
      topic_filter: { ...topic_filter, [currentDepth]: topicName },
      metric_type: displayMetricType,
      changeLevel: row.changeLevel
    };

    return fetchData({ widget_config });
  };

  const navigateToLevel = (depthKey, newCurrentLevel) => {
    dispatch({
      type: 'SET_WIDGET_CURRENT_LEVEL',
      payload: { widgetId, currentLevel: newCurrentLevel }
    });
  };
  useEffect(() => {
    handleMetricChange(selectedMetricType);
  }, []);

  const rawChartData = get(chartData, `${displayMetricType}.${currentLevel}.data`, {});
  const { columns = [], metric_data = [], rows = [] } = rawChartData;

  const multiLineChartData = buildMonthSeriesForMultiLineChart(columns, rows, metric_data, CHART_COLORS);
  const tableDataConverted = convertCstDataIntoTableCompatible(rawChartData);

  const finalData = multiLineChartData
    ?.map((e, index) => {
      const rowData = tableDataConverted?.table_rows?.[index];
      return {
        ...e,
        label: { value: rowData?.total?.toFixed(2), label: selectedMetricType[0]?.label || '' },
        subLabel: { value: rowData?.delta?.toFixed(2), label: 'Same Period Ago' }
      };
    })
    .filter(e => e.name !== 'Sample Size');

  const chartCategories = seprateDateAndNonDateColumns(columns)?.dateFields?.map(e =>
    moment(e, 'YYYY-MM').format('MMM')
  );

  useEffect(() => {
    dispatch({
      type: 'SET_WIDGET_CURRENT_LEVEL',
      payload: { widgetId, currentLevel: { root: 'All Topics' } }
    });
  }, []);

  const renderContent = () => {
    const parsedCurrentLevel = typeof currentLevel === 'string' ? JSON.parse(currentLevel) : currentLevel;

    if (activeSwitch === 'TABLE') {
      return (
        <ExpandableGrid
          fetchData={opt =>
            fetchData({ ...opt, widget_config: { ...opt.widget_config, metric_type: displayMetricType } })
          }
          getNestedData={fetchNestedData}
          columns={tableColumns}
          widgetSpecificKeys={{ widgetId }}
          metricKey={displayMetricType}
          dataCache={tableData || {}}
        />
      );
    }

    const levelLen = Object.keys(parsedCurrentLevel || {}).length;
    const ctaProps =
      levelLen < 3
        ? {
            ctaText: 'Subtopics',
            handleCtaClick: name => {
              fetchNestedData({
                topic_depth: levelLen === 1 ? 'l1' : 'l2',
                topic_filter: parsedCurrentLevel,
                primary: name,
                changeLevel: true
              });
            }
          }
        : {};

    return (
      <>
        <Breadcrumb currentLevel={parsedCurrentLevel} onLevelClick={navigateToLevel} />
        <MultiSeriesLineChart
          ref={ref}
          widgetData={chartData[currentLevel] || {}}
          categories={chartCategories}
          chartType='line'
          data={finalData || []}
          variant='compact'
          isLoading={false}
          {...ctaProps}
        />
      </>
    );
  };

  return (
    <MultiFeatureCard
      title='Topic Trends Over Time'
      subTitle={`Time Period: ${timeperiodLabel}`}
      enableSwitchTab={itemData?.enableSwitchTab}
      switchOptions={itemData?.switchOptions}
      style={{ childContainer: { minHeight: 250, paddingBottom: 15 } }}
      loading={loadingData}
      renderContent={sw => {
        setActiveSwitch(sw);
        return (
          <View>
            <View style={{ marginBottom: 16, maxWidth: 200 }}>
              <Text style={styles.label}>Metric</Text>
              <MultiSelect
                mode='single'
                options={metricTypesOptions}
                onSelect={handleMetricChange}
                selected={selectedMetricType}
              />
            </View>
            {error ? <GenericNoData /> : renderContent()}
          </View>
        );
      }}
      renderFooter={() =>
        !loadingData && <LegendBar items={itemData?.metricBasedConfigs?.[displayMetricType]?.legend} />
      }
    />
  );
});

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
    backgroundColor: 'rgba(255,255,255,0.6)',
    alignItems: 'center',
    justifyContent: 'center'
  },
  label: { marginBottom: 8, fontWeight: '400', color: '#6b7589' },
  errorText: { color: '#d9534f', textAlign: 'center', marginTop: 20 }
});

export default TopicTrendsOverTime;
